import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import {
  Plus, X, ChevronRight, ChevronDown,
  Circle, CheckCircle2, AlertCircle, RefreshCw,
} from 'lucide-react'
import ColorPicker from '../../components/ColorPicker/ColorPicker'

const PRIORITA = {
  alta:  { label: 'Alta',  dot: 'bg-red-400',    text: 'text-red-400'    },
  media: { label: 'Media', dot: 'bg-yellow-400',  text: 'text-yellow-400' },
  bassa: { label: 'Bassa', dot: 'bg-sky-400',     text: 'text-sky-400'    },
}
const PRIORITA_ORDER = { alta: 0, media: 1, bassa: 2 }

// Ordina per priorità, poi alfabeticamente per titolo (A→Z)
const sortTasks = (a, b) => {
  const pd = (PRIORITA_ORDER[a.priorita] ?? 1) - (PRIORITA_ORDER[b.priorita] ?? 1)
  if (pd !== 0) return pd
  return (a.titolo || '').localeCompare(b.titolo || '', 'it', { sensitivity: 'base' })
}

const isScaduta = (task) => {
  if (!task.scadenza || task.status === 'done') return false
  const d = new Date(task.scadenza + 'T00:00:00')
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0)
  return d < oggi
}
const formatDate = (d) => {
  if (!d) return null
  return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
}

// ─── Avatar stack ───────────────────────────────────────────────────────────────
const AvatarStack = ({ assignees, max = 3 }) => {
  if (!assignees?.length) return null
  const shown = assignees.slice(0, max)
  const rest = assignees.length - shown.length
  return (
    <div className="flex -space-x-1.5">
      {shown.map((a, i) => (
        <div
          key={a.id}
          title={a.nickname || a.full_name || 'Utente'}
          style={{ zIndex: shown.length - i }}
          className="w-5 h-5 rounded-full bg-violet-600 border border-gray-900 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0"
        >
          {a.avatar_url
            ? <img src={a.avatar_url} alt="" className="w-full h-full object-cover" />
            : (a.nickname || a.full_name || '?')[0].toUpperCase()
          }
        </div>
      ))}
      {rest > 0 && (
        <div className="w-5 h-5 rounded-full bg-gray-700 border border-gray-900 flex items-center justify-center text-gray-400 text-xs font-bold">
          +{rest}
        </div>
      )}
    </div>
  )
}

// ─── SubtaskList (mutual recursion) ─────────────────────────────────────────────
const SubtaskList = ({ parentId, allTasks, allAssignees, profiles, onToggle, onEdit, depth }) => {
  const children = allTasks
    .filter(t => t.parent_id === parentId)
    .sort(sortTasks)
  if (children.length === 0) return null
  return (
    <div className="ml-6 border-l border-gray-800 pl-3">
      {children.map(task => (
        <TaskRow key={task.id} task={task} allTasks={allTasks} allAssignees={allAssignees} profiles={profiles} onToggle={onToggle} onEdit={onEdit} depth={depth} />
      ))}
    </div>
  )
}

// ─── TaskRow ────────────────────────────────────────────────────────────────────
const TaskRow = ({ task, allTasks, allAssignees, profiles, onToggle, onEdit, depth = 0 }) => {
  const [expanded, setExpanded] = useState(true)
  const hasChildren = allTasks.some(t => t.parent_id === task.id)
  const scaduta = isScaduta(task)
  const p = PRIORITA[task.priorita] || PRIORITA.media
  const isDone = task.status === 'done'

  const taskAssigneeIds = allAssignees.filter(a => a.task_id === task.id).map(a => a.user_id)
  const assignees = profiles.filter(pr => taskAssigneeIds.includes(pr.id))

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-2 px-2 rounded-xl hover:bg-gray-800/60 group transition-colors cursor-pointer ${isDone ? 'opacity-50' : ''}`}
        style={task.colore ? { borderLeft: `3px solid ${task.colore}`, paddingLeft: '6px', borderRadius: '0 12px 12px 0' } : {}}
        onClick={() => onEdit(task)}
      >
        <button
          onClick={e => { e.stopPropagation(); setExpanded(!expanded) }}
          className={`flex-shrink-0 text-gray-600 hover:text-gray-400 transition-colors w-4 ${hasChildren ? 'visible' : 'invisible'}`}
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <button onClick={e => { e.stopPropagation(); onToggle(task) }} className="flex-shrink-0 transition-colors">
          {isDone
            ? <CheckCircle2 size={17} className="text-green-500" />
            : <Circle size={17} className="text-gray-600 hover:text-gray-400" />}
        </button>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${p.dot}`} />
        <span className={`flex-1 text-sm min-w-0 truncate ${isDone ? 'line-through text-gray-500' : 'text-white'}`}>
          {task.titolo}
        </span>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {task.scena && (
            <span className="text-[10px] text-indigo-400 border border-indigo-500/30 rounded px-1.5 py-px leading-none max-w-[80px] truncate hidden sm:inline-block">
              {task.scena}
            </span>
          )}
          {task.facoltativa && (
            <span className="text-[10px] text-gray-500 italic border border-gray-700 rounded px-1 py-px leading-none">
              facolt.
            </span>
          )}
          {task.deliverable && !task.delivered && <span className="w-2 h-2 rounded-full bg-orange-400" title="Deliverable" />}
          {task.deliverable && task.delivered && <span className="w-2 h-2 rounded-full bg-green-500" title="Consegnato" />}
          {task.is_recurring && <RefreshCw size={11} className="text-gray-600" title="Ricorrente" />}
          {scaduta
            ? <span className="flex items-center gap-1 text-xs text-red-400"><AlertCircle size={11} />{formatDate(task.scadenza)}</span>
            : task.scadenza ? <span className="text-xs text-gray-500">{formatDate(task.scadenza)}</span> : null}
          <AvatarStack assignees={assignees} />
        </div>
      </div>
      {expanded && hasChildren && (
        <SubtaskList parentId={task.id} allTasks={allTasks} allAssignees={allAssignees} profiles={profiles} onToggle={onToggle} onEdit={onEdit} depth={depth + 1} />
      )}
    </div>
  )
}

// ─── AssigneeSelector ────────────────────────────────────────────────────────────
const AssigneeSelector = ({ profiles, selected, onChange }) => (
  <div>
    <label className="block text-sm text-gray-400 mb-2">Assegnato a</label>
    <div className="flex flex-wrap gap-2">
      {profiles.map(p => {
        const isSelected = selected.includes(p.id)
        const nome = p.nickname || p.full_name || 'Utente'
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(isSelected ? selected.filter(id => id !== p.id) : [...selected, p.id])}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
              isSelected
                ? 'bg-violet-600/20 border-violet-500 text-violet-300'
                : 'border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'
            }`}
          >
            <div className="w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center text-white text-xs font-bold overflow-hidden flex-shrink-0">
              {p.avatar_url
                ? <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
                : nome[0].toUpperCase()}
            </div>
            {nome}
          </button>
        )
      })}
    </div>
  </div>
)

// ─── TaskModal ──────────────────────────────────────────────────────────────────
const TaskModal = ({ task, progetti, profiles, onClose, onSaved, onDeleted }) => {
  const { profile } = useAuth()
  const isEdit = !!task?.id

  const [titolo, setTitolo] = useState(task?.titolo || '')
  const [descrizione, setDescrizione] = useState(task?.descrizione || '')
  const [note, setNote] = useState(task?.note || '')
  const [priorita, setPriorita] = useState(task?.priorita || 'media')
  const [effort, setEffort] = useState(task?.effort || 'media')
  const [status, setStatus] = useState(task?.status || 'todo')
  const [scadenza, setScadenza] = useState(task?.scadenza || '')
  const [projectId, setProjectId] = useState(task?.project_id || '')
  const [selectedAssignees, setSelectedAssignees] = useState([])
  const [colore, setColore] = useState(task?.colore || '')
  const [isRecurring, setIsRecurring] = useState(task?.is_recurring || false)
  const [recurrenceType, setRecurrenceType] = useState(task?.recurrence_type || 'weekly')
  const [recurrenceDays, setRecurrenceDays] = useState(task?.recurrence_days || 7)
  const [deliverable, setDeliverable] = useState(task?.deliverable || false)
  const [delivered, setDelivered] = useState(task?.delivered || false)
  const [facoltativa, setFacoltativa] = useState(task?.facoltativa || false)
  const [scena, setScena] = useState(task?.scena || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [subtasks, setSubtasks] = useState([])
  const [newSubtask, setNewSubtask] = useState('')
  const [addingSubtask, setAddingSubtask] = useState(false)

  useEffect(() => {
    if (!isEdit) return
    // Carica subtask
    supabase.from('tasks').select('*').eq('parent_id', task.id).order('created_at')
      .then(({ data }) => setSubtasks(data || []))
    // Carica assegnazioni esistenti
    supabase.from('task_assignees').select('user_id').eq('task_id', task.id)
      .then(({ data }) => setSelectedAssignees((data || []).map(a => a.user_id)))
  }, [isEdit, task?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveAssignees = async (taskId, assigneeIds) => {
    await supabase.from('task_assignees').delete().eq('task_id', taskId)
    if (assigneeIds.length > 0) {
      await supabase.from('task_assignees').insert(assigneeIds.map(uid => ({ task_id: taskId, user_id: uid })))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!titolo.trim()) return
    setLoading(true); setError(null)
    const payload = {
      titolo: titolo.trim(), descrizione: descrizione || null, note: note || null,
      priorita, effort, status, scadenza: scadenza || null,
      colore: colore || null,
      project_id: projectId || null,
      assegnato_a: selectedAssignees[0] || null, // backward compat
      is_recurring: isRecurring,
      recurrence_type: isRecurring ? recurrenceType : null,
      recurrence_days: isRecurring && recurrenceType === 'custom' ? Number(recurrenceDays) : null,
      deliverable, delivered: deliverable ? delivered : false,
      facoltativa,
      scena: scena.trim() || null,
      updated_at: new Date().toISOString(),
    }
    let data, err
    if (isEdit) {
      ;({ data, error: err } = await supabase.from('tasks').update(payload).eq('id', task.id).select().single())
    } else {
      ;({ data, error: err } = await supabase.from('tasks').insert({ ...payload, creato_da: profile.id, parent_id: task?.parent_id || null }).select().single())
    }
    if (err) { setError(err.message); setLoading(false); return }
    await saveAssignees(data.id, selectedAssignees)
    onSaved(data, selectedAssignees)
    onClose()
  }

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return }
    setDeleting(true)
    await supabase.from('tasks').delete().eq('id', task.id)
    onDeleted(task.id); onClose()
  }

  const autoCompleteParent = async (updatedSubs) => {
    if (!updatedSubs.length) return
    const allDone = updatedSubs.every(s => s.status === 'done')
    if (allDone && task.status !== 'done') {
      const { data } = await supabase.from('tasks').update({ status: 'done', updated_at: new Date().toISOString() }).eq('id', task.id).select().single()
      if (data) onSaved(data, selectedAssignees)
    } else if (!allDone && task.status === 'done') {
      const { data } = await supabase.from('tasks').update({ status: 'todo', updated_at: new Date().toISOString() }).eq('id', task.id).select().single()
      if (data) onSaved(data, selectedAssignees)
    }
  }

  const handleAddSubtask = async () => {
    if (!newSubtask.trim()) return
    setAddingSubtask(true)
    const { data } = await supabase.from('tasks').insert({
      titolo: newSubtask.trim(), parent_id: task.id,
      project_id: task.project_id || null, creato_da: profile.id, priorita: 'media', status: 'todo',
    }).select().single()
    if (data) { setSubtasks(prev => [...prev, data]); onSaved(data, []) }
    setNewSubtask(''); setAddingSubtask(false)
  }

  const handleToggleSubtask = async (sub) => {
    const newStatus = sub.status === 'done' ? 'todo' : 'done'
    await supabase.from('tasks').update({ status: newStatus }).eq('id', sub.id)
    const updated = subtasks.map(s => s.id === sub.id ? { ...s, status: newStatus } : s)
    setSubtasks(updated); await autoCompleteParent(updated)
  }

  const handleDeleteSubtask = async (subId) => {
    await supabase.from('tasks').delete().eq('id', subId)
    setSubtasks(prev => prev.filter(s => s.id !== subId))
  }

  const inputCls = "w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors"

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <h2 className="text-lg font-bold text-white">{isEdit ? 'Modifica task' : 'Nuova task'}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Titolo *</label>
            <input type="text" value={titolo} onChange={e => setTitolo(e.target.value)} required autoFocus className={inputCls} placeholder="Cosa bisogna fare..." />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrizione</label>
            <textarea value={descrizione} onChange={e => setDescrizione(e.target.value)} rows={2} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none" placeholder="Dettagli..." />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Note</label>
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors resize-none" placeholder="Note interne..." />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Priorità</label>
              <select value={priorita} onChange={e => setPriorita(e.target.value)} className={inputCls}>
                <option value="alta">Alta</option><option value="media">Media</option><option value="bassa">Bassa</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Effort</label>
              <select value={effort} onChange={e => setEffort(e.target.value)} className={inputCls}>
                <option value="bassa">Basso</option><option value="media">Medio</option><option value="alta">Alto</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Stato</label>
              <select value={status} onChange={e => setStatus(e.target.value)} className={inputCls}>
                <option value="todo">Da fare</option><option value="inprogress">In corso</option><option value="done">Completata</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Scadenza</label>
              <input type="date" value={scadenza} onChange={e => setScadenza(e.target.value)} className={inputCls} />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Progetto</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls}>
              <option value="">— Nessun progetto —</option>
              {progetti.map(p => <option key={p.id} value={p.id}>{p.titolo}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Scena <span className="text-gray-600 font-normal">(opzionale)</span></label>
            <input
              type="text"
              value={scena}
              onChange={e => setScena(e.target.value)}
              className={inputCls}
              placeholder="es. Scena 1, Intro, VFX shot 03..."
            />
          </div>

          {/* Multi-assegnazione */}
          <AssigneeSelector profiles={profiles} selected={selectedAssignees} onChange={setSelectedAssignees} />

          {/* Colore etichetta */}
          <div className="bg-gray-800/50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">Colore etichetta</span>
              {colore && (
                <button type="button" onClick={() => setColore('')} className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
                  Rimuovi
                </button>
              )}
            </div>
            <ColorPicker value={colore || '#7c3aed'} onChange={setColore} label="" />
          </div>

          {/* Ricorrente */}
          <div className="bg-gray-800/50 rounded-xl p-4 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={isRecurring} onChange={e => setIsRecurring(e.target.checked)} className="w-4 h-4 rounded border-gray-600 accent-violet-500" />
              <span className="text-sm text-gray-300 flex items-center gap-2"><RefreshCw size={14} />Task ricorrente</span>
            </label>
            {isRecurring && (
              <div className="flex items-center gap-3 pl-7">
                <select value={recurrenceType} onChange={e => setRecurrenceType(e.target.value)} className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500">
                  <option value="daily">Giornaliera</option><option value="weekly">Settimanale</option><option value="monthly">Mensile</option><option value="custom">Personalizzata</option>
                </select>
                {recurrenceType === 'custom' && (
                  <div className="flex items-center gap-2">
                    <input type="number" value={recurrenceDays} onChange={e => setRecurrenceDays(e.target.value)} min={1} className="w-16 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-violet-500" />
                    <span className="text-sm text-gray-500 whitespace-nowrap">giorni</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Deliverable + Facoltativa */}
          <div className="bg-gray-800/50 rounded-xl p-4 space-y-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={deliverable} onChange={e => setDeliverable(e.target.checked)} className="w-4 h-4 rounded border-gray-600 accent-orange-500" />
              <span className="text-sm text-gray-300 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400" />Deliverable</span>
            </label>
            {deliverable && (
              <label className="flex items-center gap-3 cursor-pointer pl-7">
                <input type="checkbox" checked={delivered} onChange={e => setDelivered(e.target.checked)} className="w-4 h-4 rounded border-gray-600 accent-green-500" />
                <span className="text-sm text-gray-400">Consegnato</span>
              </label>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={facoltativa} onChange={e => setFacoltativa(e.target.checked)} className="w-4 h-4 rounded border-gray-600 accent-gray-400" />
              <span className="text-sm text-gray-300 flex items-center gap-2 italic">Facoltativa</span>
            </label>
          </div>

          {/* Subtask */}
          {isEdit && (
            <div>
              <label className="block text-sm text-gray-400 mb-2">Subtask</label>
              {subtasks.length > 0 && (
                <div className="space-y-1 mb-2">
                  {subtasks.map(sub => (
                    <div key={sub.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 group">
                      <button type="button" onClick={() => handleToggleSubtask(sub)} className="flex-shrink-0">
                        {sub.status === 'done' ? <CheckCircle2 size={15} className="text-green-500" /> : <Circle size={15} className="text-gray-600 hover:text-gray-400" />}
                      </button>
                      <span className={`flex-1 text-sm ${sub.status === 'done' ? 'line-through text-gray-500' : 'text-gray-300'}`}>{sub.titolo}</span>
                      <button type="button" onClick={() => handleDeleteSubtask(sub.id)} className="opacity-0 group-hover:opacity-100 text-gray-600 hover:text-red-400 transition-all"><X size={13} /></button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input type="text" value={newSubtask} onChange={e => setNewSubtask(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubtask() } }}
                  className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors" placeholder="Aggiungi subtask..." />
                <button type="button" onClick={handleAddSubtask} disabled={addingSubtask || !newSubtask.trim()} className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-40 transition-colors">
                  <Plus size={15} />
                </button>
              </div>
            </div>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <div className="flex gap-3 pt-2">
            {isEdit && (
              <button type="button" onClick={handleDelete} disabled={deleting}
                className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${confirmDelete ? 'bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white' : 'border border-gray-700 text-gray-500 hover:text-red-400 hover:border-red-500/40'}`}>
                {deleting ? '...' : confirmDelete ? 'Conferma eliminazione' : 'Elimina'}
              </button>
            )}
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-lg border border-gray-700 text-gray-400 hover:text-white transition-colors text-sm">Annulla</button>
            <button type="submit" disabled={loading || !titolo.trim()} className="flex-1 py-3 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium text-sm transition-colors">
              {loading ? 'Salvataggio...' : isEdit ? 'Salva' : 'Crea'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Tasks ──────────────────────────────────────────────────────────────────────
const Tasks = () => {
  const [tasks, setTasks] = useState([])
  const [allAssignees, setAllAssignees] = useState([]) // [{task_id, user_id}]
  const [progetti, setProgetti] = useState([])
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalTask, setModalTask] = useState(null)
  const [showModal, setShowModal] = useState(false)

  const fetchAll = useCallback(async () => {
    const [{ data: t }, { data: pr }, { data: pf }, { data: ta }] = await Promise.all([
      supabase.from('tasks').select('*').order('created_at', { ascending: false }),
      supabase.from('progetti').select('id, titolo, colore, status').order('titolo'),
      supabase.from('profiles').select('id, nickname, full_name, avatar_url').order('nickname'),
      supabase.from('task_assignees').select('task_id, user_id'),
    ])
    // Nascondi le task dei progetti completati o archiviati
    const chiusi = new Set((pr || []).filter(p => p.status === 'completato' || p.status === 'archiviato').map(p => p.id))
    setTasks((t || []).filter(task => !task.project_id || !chiusi.has(task.project_id)))
    setProgetti(pr || [])
    setProfiles(pf || [])
    setAllAssignees(ta || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchAll()
    const channel = supabase
      .channel('tasks-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'task_assignees' }, fetchAll)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'progetti' }, fetchAll)
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [fetchAll])

  const handleToggle = async (task) => {
    const newStatus = task.status === 'done' ? 'todo' : 'done'
    await supabase.from('tasks').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', task.id)
    const updatedTasks = tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t)
    setTasks(updatedTasks)
    if (!task.parent_id) return
    const siblings = updatedTasks.filter(t => t.parent_id === task.parent_id)
    const allDone = siblings.length > 0 && siblings.every(s => s.status === 'done')
    const parent = updatedTasks.find(t => t.id === task.parent_id)
    if (!parent) return
    if (allDone && parent.status !== 'done') {
      await supabase.from('tasks').update({ status: 'done', updated_at: new Date().toISOString() }).eq('id', task.parent_id)
      setTasks(prev => prev.map(t => t.id === task.parent_id ? { ...t, status: 'done' } : t))
    } else if (!allDone && parent.status === 'done') {
      await supabase.from('tasks').update({ status: 'todo', updated_at: new Date().toISOString() }).eq('id', task.parent_id)
      setTasks(prev => prev.map(t => t.id === task.parent_id ? { ...t, status: 'todo' } : t))
    }
  }

  const handleSaved = (savedTask, newAssigneeIds) => {
    setTasks(prev => {
      const exists = prev.find(t => t.id === savedTask.id)
      return exists ? prev.map(t => t.id === savedTask.id ? savedTask : t) : [savedTask, ...prev]
    })
    if (newAssigneeIds !== undefined) {
      setAllAssignees(prev => [
        ...prev.filter(a => a.task_id !== savedTask.id),
        ...newAssigneeIds.map(uid => ({ task_id: savedTask.id, user_id: uid }))
      ])
    }
  }

  const handleDeleted = (id) => {
    setTasks(prev => prev.filter(t => t.id !== id && t.parent_id !== id))
    setAllAssignees(prev => prev.filter(a => a.task_id !== id))
  }

  const openNew = (defaultProjectId = null) => { setModalTask({ project_id: defaultProjectId }); setShowModal(true) }
  const openEdit = (task) => { setModalTask(task); setShowModal(true) }

  const rootTasks = tasks.filter(t => !t.parent_id)
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0)
  const statTodo       = rootTasks.filter(t => t.status === 'todo').length
  const statInProgress = rootTasks.filter(t => t.status === 'inprogress').length
  const statScadute    = rootTasks.filter(t => t.status !== 'done' && t.scadenza && new Date(t.scadenza + 'T00:00:00') < oggi).length
  const statDone       = rootTasks.filter(t => t.status === 'done').length

  const byProject = {}
  rootTasks.forEach(task => {
    const key = task.project_id || '__personal__'
    if (!byProject[key]) byProject[key] = []
    byProject[key].push(task)
  })
  Object.values(byProject).forEach(arr => arr.sort(sortTasks))

  const groups = [
    ...progetti.filter(p => byProject[p.id]).map(p => ({ id: p.id, titolo: p.titolo, colore: p.colore, tasks: byProject[p.id] })),
    ...(byProject['__personal__'] ? [{ id: '__personal__', titolo: 'Senza progetto', colore: '#6b7280', tasks: byProject['__personal__'] }] : []),
  ]

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Tasks</h1>
          <p className="text-gray-500 text-sm mt-1">{rootTasks.length} task totali</p>
        </div>
        <button onClick={() => openNew()} className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm">
          <Plus size={16} />Nuova task
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Da fare',    value: statTodo,       color: 'text-gray-300',   bg: 'bg-gray-800/60'   },
          { label: 'In corso',   value: statInProgress, color: 'text-violet-400', bg: 'bg-violet-500/10' },
          { label: 'Scadute',    value: statScadute,    color: 'text-red-400',    bg: 'bg-red-500/10'    },
          { label: 'Completate', value: statDone,       color: 'text-green-400',  bg: 'bg-green-500/10'  },
        ].map(stat => (
          <div key={stat.label} className={`${stat.bg} border border-gray-800 rounded-xl px-4 py-3`}>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-24">
          <p className="text-gray-600 mb-4">Nessuna task ancora.</p>
          <button onClick={() => openNew()} className="text-violet-400 hover:text-violet-300 text-sm transition-colors">Crea la prima task →</button>
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map(group => {
            const hasScenes = group.tasks.some(t => t.scena)
            // Se ci sono scene: raggruppa, altrimenti flat
            const sceneGroups = hasScenes
              ? (() => {
                  const map = {}
                  group.tasks.forEach(t => {
                    const k = t.scena || '__none__'
                    if (!map[k]) map[k] = []
                    map[k].push(t)
                  })
                  // Ordina: scene con nome prima (alfabetico), poi __none__
                  return Object.entries(map).sort(([a], [b]) => {
                    if (a === '__none__') return 1
                    if (b === '__none__') return -1
                    return a.localeCompare(b, 'it')
                  })
                })()
              : null

            return (
              <div key={group.id} className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: group.colore }} />
                    <span className="text-sm font-semibold text-white">{group.titolo}</span>
                    <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">{group.tasks.length}</span>
                  </div>
                  <button onClick={() => openNew(group.id === '__personal__' ? null : group.id)} className="text-gray-600 hover:text-violet-400 transition-colors" title="Aggiungi task">
                    <Plus size={16} />
                  </button>
                </div>

                {hasScenes ? (
                  <div className="divide-y divide-gray-800/60">
                    {sceneGroups.map(([sceneName, sceneTasks]) => (
                      <div key={sceneName}>
                        <div className="px-4 py-2 flex items-center gap-2">
                          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            {sceneName === '__none__' ? 'Senza scena' : sceneName}
                          </span>
                          <span className="text-[10px] text-gray-700">{sceneTasks.length}</span>
                        </div>
                        <div className="px-2 pb-2">
                          {sceneTasks.map(task => (
                            <TaskRow key={task.id} task={task} allTasks={tasks} allAssignees={allAssignees} profiles={profiles} onToggle={handleToggle} onEdit={openEdit} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-2 py-2">
                    {group.tasks.map(task => (
                      <TaskRow key={task.id} task={task} allTasks={tasks} allAssignees={allAssignees} profiles={profiles} onToggle={handleToggle} onEdit={openEdit} />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <TaskModal
          task={modalTask}
          progetti={progetti}
          profiles={profiles}
          onClose={() => { setShowModal(false); setModalTask(null) }}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  )
}

export default Tasks
